import { useSession } from "next-auth/react";
import { useEffect } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";

const withNoAuth = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  const ComponentWithNoAuth: React.FC<P> = (props: any) => {
    const { data: session } = useSession();
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();

    const locale = pathname.split("/")[1] || "de";

    useEffect(() => {
      if (!session) return;

      const redirectRaw = searchParams.get("redirect");
      const redirect = redirectRaw ? decodeURIComponent(redirectRaw) : "/";

      if (!redirect.startsWith("/")) {
        router.replace(`/${locale}/`);
        return;
      }

      // Split hash from redirect (preserve #...)
      const [redirectNoHash, hash = ""] = redirect.split("#");

      // Split path and query from redirect
      const [redirectPath, redirectQuery = ""] = redirectNoHash.split("?");
      const redirectQs = new URLSearchParams(redirectQuery);

      // Take ALL current login params except "redirect" and merge into redirectQs
      searchParams.forEach((value, key) => {
        if (key === "redirect") return;

        // Don't overwrite if redirect already has that param
        if (!redirectQs.has(key)) {
          redirectQs.set(key, value);
        }
      });

      const mergedQuery = redirectQs.toString();
      const mergedRedirect =
        redirectPath + (mergedQuery ? `?${mergedQuery}` : "") + (hash ? `#${hash}` : "");

      router.replace(`/${locale}${mergedRedirect}`);
    }, [session, router, searchParams, locale]);

    return <WrappedComponent {...props} />;
  };

  return ComponentWithNoAuth;
};

export default withNoAuth;
